<template>
  <span v-if="editable && editFlag" :class="editContainerKls" v-bind="attrs">
    <el-input
      ref="inputRef"
      v-model="editContent"
      :class="inputKls"
      :size="tagSize"
      :style="{ width: `${inputWidth}px` }"
      :autofocus="true"
      @blur="handleEditBlur"
      @input="handleEditInput"
      @keydown.enter="handleEditEnter"
    />
    <span ref="tagStrutRef" :class="strutKls">{{ editContent }}</span>
  </span>
  <template v-else>
    <span
      v-if="disableTransitions"
      ref="tagRef"
      :class="containerKls"
      :style="{ backgroundColor: color }"
      v-bind="attrs"
      @click="handleClick"
      @dblclick="handleDblClick"
    >
      <span :class="ns.e('content')">
        <slot>{{ content }}</slot>
      </span>
      <el-icon v-if="closable" :class="ns.e('close')" @click.stop="handleClose">
        <Close />
      </el-icon>
    </span>
    <transition v-else :name="`${ns.namespace.value}-zoom-in-center`" appear>
      <span
        ref="tagRef"
        :class="containerKls"
        :style="{ backgroundColor: color }"
        v-bind="attrs"
        @click="handleClick"
        @dblclick="handleDblClick"
      >
        <span :class="ns.e('content')">
          <slot>{{ content }}</slot>
        </span>
        <el-icon
          v-if="closable"
          :class="ns.e('close')"
          @click.stop="handleClose"
        >
          <Close />
        </el-icon>
      </span>
    </transition>
  </template>
</template>

<script lang="ts" setup>
import { computed, nextTick, ref, useAttrs } from 'vue'
import { useDebounceFn } from '@vueuse/core'
import ElInput from '@element-plus/components/input'
import ElIcon from '@element-plus/components/icon'
import { Close } from '@element-plus/icons-vue'
import { useNamespace } from '@element-plus/hooks'
import { useFormSize } from '@element-plus/components/form'

import { tagEmits, tagProps } from './tag'

defineOptions({
  name: 'ElTag',
  inheritAttrs: false,
})
const props = defineProps(tagProps)
const emit = defineEmits(tagEmits)
const attrs = useAttrs()

const tagSize = useFormSize()
const ns = useNamespace('tag')

const tagRef = ref<HTMLSpanElement>()
const tagStrutRef = ref<HTMLSpanElement>()
const inputRef = ref<InstanceType<typeof ElInput>>()
const editFlag = ref(false)
const editContent = ref('')
const tagWidth = ref(0)
const tagOtherWidth = ref(0)

const strutKls = computed(() => [ns.b('strut'), ns.bm('strut', tagSize.value)])
const inputKls = computed(() => [ns.b('input')])
const editContainerKls = computed(() => [ns.b('edit-wrapper')])
const inputWidth = computed(() =>
  Math.min(
    Math.max(tagWidth.value || 0, props.editMinWidth),
    props.editMaxWidth
  )
)
const containerKls = computed(() => {
  const { type, hit, effect, closable, round, editable } = props
  return [
    ns.b(),
    ns.is('closable', closable),
    ns.m(type || 'primary'),
    ns.m(tagSize.value),
    ns.m(effect),
    editable && ns.m('editable'),
    ns.is('hit', hit),
    ns.is('round', round),
  ]
})

// methods
const handleClose = (event: MouseEvent) => {
  emit('close', event)
}

const handleClick = (event: MouseEvent) => {
  emit('click', event)
}

const handleDblClick = (event: MouseEvent) => {
  event.stopPropagation()
  emit('dblclick', event)
  if (!props.editable) return
  editContent.value = props.content || ''
  tagWidth.value = tagRef.value?.getBoundingClientRect().width ?? 0
  const contentWidth =
    tagRef.value?.querySelector('.wp-tag__content')?.getBoundingClientRect()
      .width ?? 0
  tagOtherWidth.value = tagWidth.value - contentWidth
  editFlag.value = true
  nextTick(() => {
    inputRef.value?.focus()
  })
}

const handleEditInput = (val: string) => {
  if (!props.editable) return
  editContent.value = val
  if (props.editResponsive) {
    handleResponsiveDebounce()
  }
}

const handleEditUpdate = () => {
  if (editContent.value) {
    // 非空字符串-更新 / 空字符串-还原
    emit('update:content', editContent.value)
  }
  editFlag.value = false
  editContent.value = ''
}

const handleEditEnter = (event: unknown) => {
  // console.log(e);
  if (!props.editable) return
  if ((event as KeyboardEvent).code === 'Enter') {
    handleEditUpdate()
  }
}

const handleEditBlur = () => {
  if (!props.editable || !props.changeOnBlur) return
  handleEditUpdate()
}

const handleResponsiveDebounce = useDebounceFn(() => {
  if (!props.editable || !props.editResponsive) return
  if (tagStrutRef.value) {
    tagWidth.value =
      (tagStrutRef.value?.getBoundingClientRect().width ?? 0) +
      tagOtherWidth.value
  }
}, props.debounce)
</script>
